import { Routes } from '@angular/router';

import { authGuard } from './core/auth/auth.guard';
import { permissionGuard } from './core/auth/permission.guard';

export const routes: Routes = [
  {
    path: 'auth/login',
    loadComponent: () =>
      import('./features/auth/login.page').then((module) => module.LoginPageComponent)
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layout/app-shell').then((module) => module.AppShellComponent),
    children: [
      {
        path: 'overview',
        loadComponent: () =>
          import('./features/overview/overview.page').then(
            (module) => module.OverviewPageComponent
          )
      },
      {
        path: 'reception',
        loadComponent: () =>
          import('./features/reception/reception.page').then((module) => module.ReceptionPageComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'hotel'
          },
          {
            path: 'hotel',
            loadComponent: () =>
              import('./features/reception/codes/reception-codes.page').then(
                (module) => module.ReceptionCodesPageComponent
              )
          },
          {
            path: 'hotel/new',
            canActivate: [permissionGuard('reception-operation', '/reception/hotel')],
            loadComponent: () =>
              import('./features/reception/create/reception-create.page').then(
                (module) => module.ReceptionCreatePageComponent
              )
          },
          {
            path: 'events',
            canActivate: [permissionGuard('reception-operation', '/reception/hotel')],
            loadComponent: () =>
              import('./features/reception/events/reception-events.page').then(
                (module) => module.ReceptionEventsPageComponent
              )
          },
          {
            path: 'events/:eventId',
            canActivate: [permissionGuard('reception-operation', '/reception/hotel')],
            loadComponent: () =>
              import('./features/reception/events/reception-event-management.page').then(
                (module) => module.ReceptionEventManagementPageComponent
              )
          },
          { path: 'codes', redirectTo: 'hotel' },
          { path: 'create', redirectTo: 'hotel/new' },
          {
            path: 'bulk',
            canActivate: [permissionGuard('reception-operation', '/reception/codes')],
            loadComponent: () =>
              import('./features/reception/bulk/reception-bulk.page').then(
                (module) => module.ReceptionBulkPageComponent
              )
          }
        ]
      },
      {
        path: 'administration',
        canActivate: [permissionGuard('administration', '/overview')],
        loadComponent: () =>
          import('./features/administration/administration.page').then(
            (module) => module.AdministrationPageComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'tenants'
          },
          {
            path: 'tenants',
            loadComponent: () =>
              import('./features/administration/tenants/administration-tenants.page').then(
                (module) => module.AdministrationTenantsPageComponent
              )
          },
          {
            path: 'tenants/new',
            canActivate: [permissionGuard('tenants-create', '/administration/tenants')],
            loadComponent: () =>
              import('./features/administration/tenants/administration-tenant-form.page').then(
                (module) => module.AdministrationTenantFormPageComponent
              )
          },
          {
            path: 'tenants/:tenantId/edit',
            canActivate: [permissionGuard('tenants-manage', '/administration/tenants')],
            loadComponent: () =>
              import('./features/administration/tenants/administration-tenant-form.page').then(
                (module) => module.AdministrationTenantFormPageComponent
              )
          },
          {
            path: 'locations',
            loadComponent: () =>
              import('./features/administration/locations/administration-locations.page').then(
                (module) => module.AdministrationLocationsPageComponent
              )
          },
          {
            path: 'locations/new',
            canActivate: [permissionGuard('locations-manage', '/administration/locations')],
            loadComponent: () =>
              import('./features/administration/locations/administration-location-form.page').then(
                (module) => module.AdministrationLocationFormPageComponent
              )
          },
          {
            path: 'locations/:locationId/edit',
            canActivate: [permissionGuard('locations-manage', '/administration/locations')],
            loadComponent: () =>
              import('./features/administration/locations/administration-location-form.page').then(
                (module) => module.AdministrationLocationFormPageComponent
              )
          },
          {
            path: 'locations/:locationId/infrastructure',
            loadComponent: () =>
              import('./features/administration/locations/infrastructure/administration-infrastructure.page').then(
                (module) => module.AdministrationInfrastructurePageComponent
              )
          },
          {
            path: 'locations/:locationId/infrastructure/rooms/new',
            canActivate: [permissionGuard('units-manage', '/administration/locations')],
            loadComponent: () =>
              import('./features/administration/locations/infrastructure/administration-room-assignment.page').then(
                (module) => module.AdministrationRoomAssignmentPageComponent
              )
          },
          {
            path: 'locations/:locationId/infrastructure/rooms/:spaceId/edit',
            canActivate: [permissionGuard('units-manage', '/administration/locations')],
            loadComponent: () =>
              import('./features/administration/locations/infrastructure/administration-room-assignment.page').then(
                (module) => module.AdministrationRoomAssignmentPageComponent
              )
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./features/administration/users/administration-users.page').then(
                (module) => module.AdministrationUsersPageComponent
              )
          },
          {
            path: 'users/new',
            canActivate: [permissionGuard('users-manage', '/administration/users')],
            loadComponent: () =>
              import('./features/administration/users/administration-user-form.page').then(
                (module) => module.AdministrationUserFormPageComponent
              )
          },
          {
            path: 'users/:userId/edit',
            canActivate: [permissionGuard('users-manage', '/administration/users')],
            loadComponent: () =>
              import('./features/administration/users/administration-user-form.page').then(
                (module) => module.AdministrationUserFormPageComponent
              )
          },
          {
            path: 'controller-vendors',
            canActivate: [permissionGuard('vendor-catalogs-view', '/administration/tenants')],
            loadComponent: () =>
              import('./features/administration/controller-vendors/administration-controller-vendors.page').then(
                (module) => module.AdministrationControllerVendorsPageComponent
              )
          },
          {
            path: 'controller-vendors/new',
            canActivate: [permissionGuard('vendor-catalogs-manage', '/administration/controller-vendors')],
            loadComponent: () =>
              import('./features/administration/controller-vendors/administration-controller-vendor-form.page').then(
                (module) => module.AdministrationControllerVendorFormPageComponent
              )
          },
          {
            path: 'controller-vendors/:vendorId/edit',
            canActivate: [permissionGuard('vendor-catalogs-manage', '/administration/controller-vendors')],
            loadComponent: () =>
              import('./features/administration/controller-vendors/administration-controller-vendor-form.page').then(
                (module) => module.AdministrationControllerVendorFormPageComponent
              )
          },
          {
            path: 'pms-vendors',
            canActivate: [permissionGuard('vendor-catalogs-view', '/administration/tenants')],
            loadComponent: () =>
              import('./features/administration/pms-vendors/administration-pms-vendors.page').then(
                (module) => module.AdministrationPmsVendorsPageComponent
              )
          },
          {
            path: 'pms-vendors/new',
            canActivate: [permissionGuard('vendor-catalogs-manage', '/administration/pms-vendors')],
            loadComponent: () =>
              import('./features/administration/pms-vendors/administration-pms-vendor-form.page').then(
                (module) => module.AdministrationPmsVendorFormPageComponent
              )
          },
          {
            path: 'pms-vendors/:vendorId/edit',
            canActivate: [permissionGuard('vendor-catalogs-manage', '/administration/pms-vendors')],
            loadComponent: () =>
              import('./features/administration/pms-vendors/administration-pms-vendor-form.page').then(
                (module) => module.AdministrationPmsVendorFormPageComponent
              )
          },
          {
            path: 'infrastructure',
            redirectTo: 'locations'
          }
        ]
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview'
      }
    ]
  },
  {
    path: '**',
    redirectTo: ''
  }
];
